import { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  FormControlLabel,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import { useTranslation } from "i18n-fe";
import { ScoreEntry, focusNextGridCell } from "common-fe";
import {
  scoreStepFor,
  ELIMINATION_REASONS,
  ERROR_AMOUNT_SOURCES,
  errorTierForOccurrence,
  nextErrorOccurrence,
  retierErrorOccurrences,
  ordinal,
  sheetSection as sharedSheetSection,
  isSectionJudged as sharedIsSectionJudged,
  // #2676 — same score-common subtotal helpers #2015 used on the RideEditor
  // branch (weightedMovementTotal/weightedCollectiveTotal), reused here so
  // this grid's subtotal rows never reimplement a local summing helper.
  formatPoints,
  weightedMovementTotal,
  weightedCollectiveTotal,
  // #2677 — the bottom-of-sheet final raw score + percentage, per booth.
  // calculateScoreSheet is the SAME engine score-api runs at complete time
  // (packages/score-common/src/utils/calculation.js): it nets errors of
  // course/time faults off totalPoints and zeroes an eliminated sheet, so
  // this never hand-rolls that arithmetic (AC4) and never prints a
  // cheerful percentage for an eliminated booth (AC5). Called directly
  // (not resolveScoreSheetTotals, which prefers a sheet's FROZEN total) so
  // this reads live off `sheets`/`test` exactly like the subtotal rows
  // above (AC6) — never a server-frozen sheet.totalPoints.
  calculateScoreSheet,
  formatPercentage,
} from "score-common";
import SheetStateChip, { deriveSheetState } from "./SheetStateChip";

// Card #2411 — the numeric-only score grid. Renders ONE page for a whole
// panel of numericOnly ScoreSheets (one column per booth/judge) instead of
// RideEditor's tab-per-sheet editor. This file is deliberately SEPARATE
// from RideEditor.jsx: #2410/#2412 also touch that file, and the card's own
// "watch out" note says not to refactor the tabbed editor while building
// this. RideEditor.jsx only branches to this component and passes plain
// props/callbacks down — every mutation here goes through onPatchSheet
// (writes into the shared `sheets` array RideEditor.jsx owns) and
// onSaveSheet (the same single-sheet PUT the tabbed editor's own Save
// button already makes, scoped to one sheet at a time — batching that
// across the whole panel is #2466's "save fan-out", out of scope here).
//
// AC6 — numericOnly is read straight off each sheet by the caller
// (RideEditor.jsx only renders this component when every sheet on the ride
// already has numericOnly === true); this component itself never reads or
// re-derives it from the class.
//
// Tab order (#2465) originally needed no wiring: DOM order matched visual
// order (row-major, booth by booth within a movement), so native Tab just
// worked. #2714 reversed that call: a judge enters one whole SHEET at a
// time, so walking every booth on a movement row before the next movement
// was the bug, not the feature — DOM order (rows are movements, columns are
// sheets) can't express "down one sheet's movements", so both Tab and Enter
// now go through the same explicit `getOrderedCells` (see
// `orderedGridCells` above) fed into gridCellNav's shared
// `focusNextGridCell` (common-fe, shared with scribe-fe's class grid, whose
// own row-per-ride shape is unaffected — it passes no such override).
// Confirm-all (#2467) is IN scope here: see onConfirmSheet/onConfirmAll
// below, mirroring the #2466 Save/Save-all shape (a per-booth action plus a
// sequential fan-out over it).
//
// Follow-up fix — #2714 originally only reordered the MOVEMENT cells; the
// single COLLECTIVES row kept plain `focusNextGridCell` with no override
// (document order), so Enter off a sheet's last movement stopped dead
// instead of continuing into that same sheet's collectives (reachable by
// Tab, not by Enter — a stumble on every ride for a scribe typing fast).
// Per-judge entry order is ONE list, movements then that judge's own
// collectives, sheet by sheet — `orderedGridCells` below now produces that
// single sequence and both the movement and collective cells are wired to
// it.

// Duplicated on purpose, not imported from RideEditor.jsx (that file is
// shared with #2410/#2412 and the card's scope note says not to touch its
// internals) — this is the same tiny, pure lookup RideEditor.jsx's own
// judgePosition() does off klass.judgeAssignments.
function judgePositionFor(klass, judgeId) {
  const assignments = klass?.judgeAssignments;
  if (!assignments) return null;
  for (const [position, ids] of Object.entries(assignments)) {
    if ((ids || []).includes(judgeId)) return position;
  }
  return null;
}

function findMovementScore(sheet, movementNumber) {
  return sheet?.movementScores?.find((m) => m.movementNumber === movementNumber)?.score;
}

// Card #2468 — per-booth section filtering on a section-judged (young-horse
// two-judge) ride. RideEditor.jsx already has this exact decision
// (sectionForSheet, #2035) for its tabbed editor, which HIDES a whole
// movement row for a judge outside their section. This grid can't do that:
// a movement row is shared across every booth's column, so hiding a row for
// one booth would hide it for every booth. Design choice (card comment):
// grey out the cell instead — see isOutOfSectionFor below, used per-cell in
// the render, not per-row.
//
// Same shared score-common primitives RideEditor.jsx's sectionForSheet
// already uses (sharedSheetSection/sharedIsSectionJudged); duplicated here
// rather than imported from RideEditor.jsx for the same "don't touch the
// shared tabbed-editor internals" reason as judgePositionFor/
// nextRuleErrorFor above. This grid only ever renders ONE test for the
// whole panel (unlike RideEditor.jsx's per-sheet `tests` map), so these
// take `test` singular instead of a map.
function sheetSectionFromScores(test, sheet) {
  return sharedSheetSection(test?.movements, sheet);
}

// RideEditor.jsx's own isSectionJudgedRide computes hasSections PER SHEET
// (tests?.[s.testId]?.movements, since its tabbed editor can have sheets on
// different tests). This grid instead reads hasSections off the single
// `test` prop for the whole panel. That's fine here, not a divergent
// re-derivation: RideEditor.jsx only ever mounts this grid with one shared
// `test` for every column (see its render call, and #2411's own doc
// comment), so `test.movements` already IS what every column renders.
function isSectionJudgedGrid(klass, test, sheets) {
  const list = sheets || [];
  const hasSections = (test?.movements ?? []).some((m) => m.section);
  if (!hasSections) return false;
  const scored = new Set(list.map((s) => sheetSectionFromScores(test, s)).filter(Boolean));
  if (sharedIsSectionJudged(scored)) return true;
  return klass?.qualityJudging?.topology === "two-judge";
}

function sectionForPositionGrid(klass, position) {
  const qj = klass?.qualityJudging;
  if (qj?.topology !== "two-judge") return null;
  if (position && position === qj.qualityPosition) return "quality";
  if (position === "C") return "technical";
  return null;
}

// Which section a booth's column may score — mirrors RideEditor.jsx's
// sectionForSheet (#2035) exactly: this sheet's own scored movements decide
// first, falling back to the class's operator-entered qualityJudging field
// only when this sheet has no marks yet. Returns null when the ride isn't
// section-judged at all, or the section is genuinely unknowable — either
// case leaves every movement editable for that column, same as before #2468.
function sectionForSheetGrid(test, klass, sheets, sheet) {
  if (!isSectionJudgedGrid(klass, test, sheets)) return null;
  const derived = sheetSectionFromScores(test, sheet);
  if (derived) return derived;
  const position = judgePositionFor(klass, sheet?.judgeId) || sheet?.judgeId || null;
  return sectionForPositionGrid(klass, position);
}

function isScoredZero(value) {
  return typeof value === "number" && value === 0;
}

function formatDeductionAmount(points, percent) {
  if (typeof points === "number") return `−${points} pt${points === 1 ? "" : "s"}`;
  if (typeof percent === "number") return `−${percent}%`;
  return "";
}

// Mirrors RideEditor.jsx's own nextRuleError() — same shared score-common
// primitives (errorTierForOccurrence/nextErrorOccurrence/ordinal), so the
// Nth error of course resolves to the same deduction here as it does in the
// tabbed editor. Duplicated for the same "don't touch the shared file"
// reason as judgePositionFor above.
function nextRuleErrorFor(errors, eliminations, tiers) {
  const occurrence = nextErrorOccurrence(errors) + (eliminations ?? []).filter((e) => e.fromError).length;
  const tier = errorTierForOccurrence(tiers, occurrence) ?? {};
  const label = `${ordinal(occurrence)} error of course`;
  if (tier.eliminate) {
    return { eliminate: true, label };
  }
  const entry = { label, amountSource: ERROR_AMOUNT_SOURCES.RULE };
  if (tier.percent !== undefined) entry.percent = tier.percent;
  else entry.points = tier.points ?? 0;
  return entry;
}

function errorLabel(e) {
  const amount = formatDeductionAmount(e.points, e.percent);
  return `${e.label}: ${amount}`;
}

// Card #2683 — the mnemonic key that opens a movement cell's gavel modal
// without a mouse. "g" only: not a digit or "." (both valid score-box
// characters), and not used anywhere else in this file's onKeyDown chain.
const GAVEL_KEY = "g";

// Card #2683 — derived, never stored: a gavel renders red when THIS judge's
// sheet already carries an error or elimination on THIS movement, grey
// otherwise. Reads straight off the same sheet.errors/sheet.eliminations
// arrays the sheet-level rows below already render, so nothing extra is
// ever persisted to drive the colour (AC6).
function hasEntryOnMovement(sheet, movementNumber) {
  return (
    (sheet.errors ?? []).some((e) => e.movementNumber === movementNumber) ||
    (sheet.eliminations ?? []).some((e) => e.movementNumber === movementNumber)
  );
}

// Card #2683 — NON-COMMITTING preview of the tier the NEXT error of course
// would resolve to, for the "(tier)" text in the gavel's penalty menu item.
// Same occurrence math as nextRuleErrorFor above (nextErrorOccurrence +
// fromError eliminations), but stops short of writing anything — it only
// reads errorTiers (the resolveErrorTiers prop, catalog/scheme-derived), so
// no rule figure is ever hard-coded here (AC7).
function previewNextErrorTier(sheet, tiers) {
  const errors = sheet?.errors ?? [];
  const eliminations = sheet?.eliminations ?? [];
  const occurrence = nextErrorOccurrence(errors) + eliminations.filter((e) => e.fromError).length;
  return errorTierForOccurrence(tiers, occurrence) ?? null;
}

function tierPreviewLabel(tier, t) {
  if (!tier) return "";
  if (tier.eliminate) return t("rideEditor.numericGrid.gavelPenaltyTierEliminate");
  return formatDeductionAmount(tier.points, tier.percent);
}

export default function NumericOnlyScoreGrid({
  test,
  klass,
  sheets,
  errorTiers,
  onPatchSheet,
  onSaveSheet,
  savingSheetId,
  saveErrors,
  onSaveAll,
  savingAll,
  saveAllResult,
  onConfirmSheet,
  confirmingSheetId,
  confirmErrors,
  onConfirmAll,
  confirmingAll,
  confirmAllRan,
  onCorrectSheet,
}) {
  const { t } = useTranslation("scoreadmin");
  const hasErrorConcept = (errorTiers ?? []).length > 0;
  const hasOpenSheet = sheets.some((s) => deriveSheetState(s) === "OPEN");

  const [elimMenu, setElimMenu] = useState(null); // { anchorEl, sheetId } | null
  const [errorPopover, setErrorPopover] = useState(null); // { anchorEl, sheetId } | null
  const [errorMode, setErrorMode] = useState(ERROR_AMOUNT_SOURCES.RULE);
  const [errorLabelDraft, setErrorLabelDraft] = useState("");
  const [errorAmountType, setErrorAmountType] = useState("points");
  const [errorAmountValue, setErrorAmountValue] = useState("");

  // Card #2683 — the per-movement gavel modal.
  // { sheetId, movementNumber, step: "menu"|"deduction"|"custom",
  //   deductionReason, deductionAmount, customReason } | null
  const [gavelModal, setGavelModal] = useState(null);

  // Card #2683 — one native <input> ref per movement cell, keyed
  // `${sheetId}-${movementNumber}`, via ScoreEntry's inputRef prop. Both the
  // gavel button's click and the "g" keyboard shortcut open the SAME modal
  // instance, and closeGavel below reads this same map so either path
  // returns focus to the exact score box it was opened from (AC14).
  const cellRefs = useRef({});
  const cellRefKey = (sheetId, movementNumber) => `${sheetId}-${movementNumber}`;
  const setCellRef = (sheetId, movementNumber) => (el) => {
    cellRefs.current[cellRefKey(sheetId, movementNumber)] = el;
  };

  // Follow-up fix — the collectives row's own native <input> refs, keyed the
  // same shape as cellRefs above but in a SEPARATE map: a collective's own
  // `movementNumber` (e.g. the "Gaits" collective is movementNumber 1) would
  // otherwise collide with an actual movement cell of the same number if
  // they shared one map.
  const collectiveCellRefs = useRef({});
  const collectiveCellRefKey = (sheetId, movementNumber) => `${sheetId}-${movementNumber}`;
  const setCollectiveCellRef = (sheetId, movementNumber) => (el) => {
    collectiveCellRefs.current[collectiveCellRefKey(sheetId, movementNumber)] = el;
  };

  // #2714 — the movements table renders rows-outer/sheets-inner (rows are
  // movements, columns are sheets), so DOM order is horizontal and can't be
  // the Tab/Enter order: a paper-scoring judge enters one whole sheet at a
  // time, so the wanted order is vertical, down one sheet's movements
  // before moving to the next sheet. This reads that order straight off the
  // same cellRefs map #2683's gavel modal already keeps (sheets outer,
  // movements inner — the reverse of the DOM's own nesting), and is handed
  // to gridCellNav's focusNextGridCell as `getOrderedCells`, which is what
  // turns ON Tab handling for this grid without touching scribe-fe's
  // ClassGridRow (its call sites pass no options, so Tab stays untouched
  // there). An out-of-section movement never gets a ref (setCellRef is only
  // reached on the editable-cell branch below), so it's simply absent from
  // this list rather than a null gap.
  //
  // Follow-up fix — extended to a SINGLE ordered list per sheet: that
  // sheet's movement cells, THEN that same sheet's collective cells, before
  // moving to the next sheet. One judge's whole sheet — movements and
  // collectives alike — gets entered before the next judge's, matching how
  // a paper scoresheet actually reads. Both the movement cells' onKeyDown
  // (below) and the collectives row's onKeyDown now pass this SAME function
  // as `getOrderedCells`, so Enter/Shift+Enter (and Tab/Shift+Tab) walk one
  // continuous sequence instead of two independent regimes that used to
  // stop dead at the movement/collectives boundary.
  const orderedGridCells = () =>
    sheets.flatMap((s) => [
      ...movements.map((m) => cellRefs.current[cellRefKey(s.id, m.movementNumber)]),
      ...collectives.map((c) => collectiveCellRefs.current[collectiveCellRefKey(s.id, c.movementNumber)]),
    ]);

  // Card #2683 — same isPercentErrorScheme pattern as scribe-fe's
  // ScoringForm.jsx: whether this test's error-of-course schedule deducts
  // in percent (FEI/young-horse schemes) or points (National default).
  // Decides the "Point deduction…" vs "Percent deduction…" wording AND
  // which field a manual gavel deduction writes into.
  const isPercentErrorScheme = (errorTiers ?? []).some((tier) => tier.percent !== undefined);

  const movements = test?.movements ?? [];
  const collectives = test?.collectives ?? [];

  const sheetById = (id) => sheets.find((s) => s.id === id) ?? null;

  // Card #2468 — each booth's own section (null on a non-section-judged
  // ride, or when the section is genuinely unknowable — see
  // sectionForSheetGrid's doc comment). Computed once per render, off the
  // whole `sheets` array, not per-cell.
  const sectionForSheetId = Object.fromEntries(
    sheets.map((s) => [s.id, sectionForSheetGrid(test, klass, sheets, s)])
  );
  const isOutOfSectionFor = (sheet, movement) => {
    const section = sectionForSheetId[sheet.id];
    return Boolean(section) && Boolean(movement.section) && movement.section !== section;
  };

  const setMovementScoreFor = (sheetId, movementNumber, value) => {
    const sheet = sheetById(sheetId);
    if (!sheet) return;
    const existing = sheet.movementScores ?? [];
    const idx = existing.findIndex((m) => m.movementNumber === movementNumber);
    if (value === "") {
      onPatchSheet(sheetId, { movementScores: existing.filter((m) => m.movementNumber !== movementNumber) });
      return;
    }
    const patch = { movementNumber, score: Number(value) };
    const next =
      idx >= 0 ? existing.map((m, i) => (i === idx ? { ...m, ...patch } : m)) : [...existing, patch];
    onPatchSheet(sheetId, { movementScores: next });
  };

  const setCollectiveScoreFor = (sheetId, key, value) => {
    const sheet = sheetById(sheetId);
    if (!sheet) return;
    const prevEntry = sheet.collectives?.[key] ?? {};
    onPatchSheet(sheetId, {
      collectives: { ...(sheet.collectives ?? {}), [key]: { ...prevEntry, score: value === "" ? undefined : value } },
    });
  };

  const openElimMenu = (event, sheetId) => setElimMenu({ anchorEl: event.currentTarget, sheetId });
  const closeElimMenu = () => setElimMenu(null);

  // Card #2683 — extended with a third, optional movementNumber param. The
  // default of null preserves the existing sheet-level "Eliminate…" call
  // site (openElimMenu/elimMenu below) unchanged: it never passes a third
  // argument, so it keeps writing an entry with no movementNumber and
  // keeps closing the sheet-level Menu, exactly as before this card (AC9).
  // A gavel-originated call passes movementNumber, which stamps the new
  // eliminations[] entry and closes the gavel modal instead.
  const addEliminationFor = (sheetId, reason, movementNumber = null) => {
    const sheet = sheetById(sheetId);
    if (!sheet) return;
    const entry = { id: crypto.randomUUID(), reason };
    if (movementNumber !== null) entry.movementNumber = movementNumber;
    onPatchSheet(sheetId, {
      eliminations: [...(sheet.eliminations ?? []), entry],
    });
    if (movementNumber !== null) {
      closeGavel();
    } else {
      closeElimMenu();
    }
  };

  const removeEliminationFor = (sheetId, id) => {
    const sheet = sheetById(sheetId);
    if (!sheet) return;
    const target = (sheet.eliminations ?? []).find((e) => e.id === id);
    const nextEliminationsRaw = (sheet.eliminations ?? []).filter((e) => e.id !== id);
    if (target?.fromError) {
      const { errors, eliminations } = retierErrorOccurrences(sheet.errors ?? [], nextEliminationsRaw, errorTiers);
      onPatchSheet(sheetId, { errors, eliminations });
    } else {
      onPatchSheet(sheetId, { eliminations: nextEliminationsRaw });
    }
  };

  const openErrorPopover = (event, sheetId) => {
    setErrorMode(hasErrorConcept ? ERROR_AMOUNT_SOURCES.RULE : ERROR_AMOUNT_SOURCES.MANUAL);
    setErrorLabelDraft("");
    setErrorAmountType("points");
    setErrorAmountValue("");
    setErrorPopover({ anchorEl: event.currentTarget, sheetId });
  };
  const closeErrorPopover = () => setErrorPopover(null);

  const confirmAddError = () => {
    if (!errorPopover) return;
    const { sheetId } = errorPopover;
    const sheet = sheetById(sheetId);
    if (!sheet) return;
    if (errorMode === ERROR_AMOUNT_SOURCES.RULE) {
      const outcome = nextRuleErrorFor(sheet.errors ?? [], sheet.eliminations ?? [], errorTiers);
      if (outcome.eliminate) {
        onPatchSheet(sheetId, {
          eliminations: [
            ...(sheet.eliminations ?? []),
            { id: crypto.randomUUID(), reason: outcome.label, fromError: true },
          ],
        });
      } else {
        onPatchSheet(sheetId, {
          errors: [...(sheet.errors ?? []), { id: crypto.randomUUID(), ...outcome }],
        });
      }
    } else {
      const label = errorLabelDraft.trim();
      if (!label) return;
      const amountValue = Number(errorAmountValue);
      if (!Number.isFinite(amountValue)) return;
      const entry = {
        id: crypto.randomUUID(),
        label,
        amountSource: ERROR_AMOUNT_SOURCES.MANUAL,
        [errorAmountType]: amountValue,
      };
      onPatchSheet(sheetId, { errors: [...(sheet.errors ?? []), entry] });
    }
    closeErrorPopover();
  };

  const removeErrorFor = (sheetId, id) => {
    const sheet = sheetById(sheetId);
    if (!sheet) return;
    const nextErrorsRaw = (sheet.errors ?? []).filter((e) => e.id !== id);
    const { errors, eliminations } = retierErrorOccurrences(nextErrorsRaw, sheet.eliminations ?? [], errorTiers);
    onPatchSheet(sheetId, { errors, eliminations });
  };

  // Card #2683 — the gavel modal's open/close. openGavel resets every
  // sub-step draft field so a leftover value from a previous movement's
  // deduction/custom step never bleeds into a fresh open. closeGavel reads
  // the ref map by the modal's OWN sheetId/movementNumber (captured before
  // the state clears) so Done/Escape/backdrop all return focus to the exact
  // score box the modal was opened from (AC14), regardless of whether it
  // was opened by click or by the "g" key.
  const openGavel = (sheetId, movementNumber) =>
    setGavelModal({ sheetId, movementNumber, step: "menu", deductionReason: "", deductionAmount: "", customReason: "" });

  const closeGavel = () => {
    const key = gavelModal ? cellRefKey(gavelModal.sheetId, gavelModal.movementNumber) : null;
    setGavelModal(null);
    const el = key ? cellRefs.current[key] : null;
    if (el) requestAnimationFrame(() => el.focus());
  };

  const backToGavelMenu = () => setGavelModal((prev) => (prev ? { ...prev, step: "menu" } : prev));

  // Card #2683 — same nextRuleErrorFor logic confirmAddError's RULE branch
  // already uses, but stamps movementNumber on the entry it creates. Commits
  // immediately (no sub-step) — clicking "Error of course (penalty)" in the
  // gavel menu both resolves the tier AND writes it in one action.
  const addRuleErrorFor = (sheetId, movementNumber) => {
    const sheet = sheetById(sheetId);
    if (!sheet) return;
    const outcome = nextRuleErrorFor(sheet.errors ?? [], sheet.eliminations ?? [], errorTiers);
    if (outcome.eliminate) {
      onPatchSheet(sheetId, {
        eliminations: [
          ...(sheet.eliminations ?? []),
          { id: crypto.randomUUID(), reason: outcome.label, fromError: true, movementNumber },
        ],
      });
    } else {
      onPatchSheet(sheetId, {
        errors: [...(sheet.errors ?? []), { id: crypto.randomUUID(), ...outcome, movementNumber }],
      });
    }
    closeGavel();
  };

  // Card #2683 — the gavel's "Point deduction…" / "Percent deduction…" sub-
  // form commit. Writes one errors[] entry with amountSource MANUAL (never
  // RULE — this is an admin-typed amount, not a resolved tier), stamping
  // movementNumber, mirroring confirmAddError's MANUAL branch above.
  const addManualDeductionFor = (sheetId, movementNumber, label, amountType, amountValue) => {
    const sheet = sheetById(sheetId);
    if (!sheet) return;
    const entry = {
      id: crypto.randomUUID(),
      label,
      amountSource: ERROR_AMOUNT_SOURCES.MANUAL,
      movementNumber,
      [amountType]: amountValue,
    };
    onPatchSheet(sheetId, { errors: [...(sheet.errors ?? []), entry] });
    closeGavel();
  };

  // #2466 — the whole-panel "Save" fan-out result. A booth's own per-column
  // save error (saveErrors[s.id], rendered in the save row below) is what
  // lets the operator retry just that one booth; this banner is the
  // page-level echo of the same outcome so a partial failure never reads as
  // a blanket "saved" or a blanket "save failed" (AC2/AC3) — it names every
  // booth in failedPositions by its judge position, or says every booth
  // saved when that array comes back empty.
  const failedPositions = saveAllResult?.failedPositions ?? [];
  const saveAllRanWithFailures = saveAllResult !== null && failedPositions.length > 0;
  const saveAllRanClean = saveAllResult !== null && failedPositions.length === 0;

  // #2467 — the whole-panel "Confirm All" fan-out result. Deliberately NOT
  // a stored result object like saveAllResult above: this is derived live
  // off confirmErrors on every render, so a later individual Confirm retry
  // on one previously-failed booth (which clears confirmErrors[sheetId] to
  // null) drops that booth out of this list for free the next render, with
  // no separate "clear the stale result" step to remember. That's the exact
  // bug the #2466 judge flagged in the Save-All banner — see this file's
  // saveAllResult comment above, which never got that fix.
  const confirmFailedPositions = sheets
    .filter((s) => confirmErrors?.[s.id])
    .map((s) => t("rideEditor.judgeLabel", { position: judgePositionFor(klass, s.judgeId) || s.judgeId }));
  const confirmAllRanWithFailures = confirmAllRan && confirmFailedPositions.length > 0;
  const confirmAllRanClean = confirmAllRan && confirmFailedPositions.length === 0;

  // Card #2683 — the gavel modal's derived display values, off gavelModal's
  // own sheetId/movementNumber. All null when the modal is closed.
  const gavelSheet = gavelModal ? sheetById(gavelModal.sheetId) : null;
  const gavelMovement = gavelModal ? movements.find((m) => m.movementNumber === gavelModal.movementNumber) : null;
  const gavelPosition = gavelSheet ? judgePositionFor(klass, gavelSheet.judgeId) || gavelSheet.judgeId : null;
  const gavelTier = gavelSheet ? previewNextErrorTier(gavelSheet, errorTiers) : null;

  // Card #2684 — same sheet.errors/eliminations arrays hasEntryOnMovement
  // above already reads, filtered down to this one movement so the "Recorded
  // on this movement" block and hasEntryOnMovement's gavel colour always
  // agree: nothing new is ever stored to drive either one (AC5).
  const gavelRecordedErrors = gavelSheet
    ? (gavelSheet.errors ?? []).filter((e) => e.movementNumber === gavelModal.movementNumber)
    : [];
  const gavelRecordedEliminations = gavelSheet
    ? (gavelSheet.eliminations ?? []).filter((e) => e.movementNumber === gavelModal.movementNumber)
    : [];

  // #2467 (judge finding D3) — a clean Confirm All on a noSignatureNeeded
  // panel (the only kind this grid ever shows, #2407) stamps completedAt on
  // every booth in the SAME write as marksConfirmedAt, so hasOpenSheet flips
  // to false the instant the run finishes. Wrapping the whole banner Stack
  // in `hasOpenSheet &&` (as Save-All's borrowed placement did) would
  // unmount the success banner before it ever rendered — Save never hits
  // this because saving alone never closes a sheet. So only the BUTTON row
  // is gated on hasOpenSheet; every banner (save or confirm) renders off its
  // own result state regardless of whether any sheet is still open.
  return (
    <Box data-testid="numeric-only-grid">
      <Stack spacing={1} sx={{ mb: 2 }}>
        {hasOpenSheet && (
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="contained"
              size="small"
              loading={savingAll}
              data-testid="numeric-only-grid-save-all"
              onClick={onSaveAll}
            >
              {t("rideEditor.numericGrid.saveAll")}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              loading={confirmingAll}
              data-testid="numeric-only-grid-confirm-all"
              onClick={onConfirmAll}
            >
              {t("rideEditor.numericGrid.confirmAll")}
            </Button>
          </Stack>
        )}
        {saveAllRanWithFailures && (
          <Alert severity="error" data-testid="numeric-only-grid-save-all-error">
            {t("rideEditor.numericGrid.saveAllPartialFailure", { positions: failedPositions.join(", ") })}
          </Alert>
        )}
        {saveAllRanClean && (
          <Alert severity="success" data-testid="numeric-only-grid-save-all-success">
            {t("rideEditor.numericGrid.saveAllSuccess")}
          </Alert>
        )}
        {confirmAllRanWithFailures && (
          <Alert severity="error" data-testid="numeric-only-grid-confirm-all-error">
            {t("rideEditor.numericGrid.confirmAllPartialFailure", { positions: confirmFailedPositions.join(", ") })}
          </Alert>
        )}
        {confirmAllRanClean && (
          <Alert severity="success" data-testid="numeric-only-grid-confirm-all-success">
            {t("rideEditor.numericGrid.confirmAllSuccess")}
          </Alert>
        )}
      </Stack>
      <TableContainer sx={{ mb: 3 }}>
        <Table size="small" data-testid="numeric-only-grid-table">
          <TableHead>
            <TableRow>
              <TableCell>{t("rideEditor.numericGrid.descriptionHeader")}</TableCell>
              {sheets.map((s) => {
                const position = judgePositionFor(klass, s.judgeId) || s.judgeId || "?";
                const state = deriveSheetState(s);
                return (
                  <TableCell key={s.id} data-testid={`numeric-only-grid-header-${s.id}`}>
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2" color="text.primary">
                        {t("rideEditor.judgeLabel", { position })}
                        {s.completedAt ? " ✓" : ""}
                      </Typography>
                      <SheetStateChip state={state} />
                    </Stack>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {movements.map((m) => (
              <TableRow key={`movement-${m.movementNumber}`} data-testid={`numeric-only-grid-movement-row-${m.movementNumber}`}>
                <TableCell>
                  <Typography variant="body2" data-testid={`numeric-only-grid-movement-desc-${m.movementNumber}`}>
                    {m.movementNumber}. {m.description}
                  </Typography>
                </TableCell>
                {sheets.map((s) => {
                  // #2468 — a movement outside this booth's own section on a
                  // section-judged ride is greyed out: no input at all (never
                  // shown or keyed, the #2035 rule this card extends to the
                  // grid), rendered before the value is even read so a stray
                  // cross-section mark (data drift, see sectionForSheetGrid's
                  // doc comment) can never leak into a cell this booth
                  // doesn't judge.
                  if (isOutOfSectionFor(s, m)) {
                    return (
                      <TableCell
                        key={s.id}
                        data-testid={`numeric-only-grid-score-cell-${s.id}-${m.movementNumber}`}
                        sx={{ bgcolor: "action.disabledBackground" }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          data-testid={`numeric-only-grid-out-of-section-${s.id}-${m.movementNumber}`}
                        >
                          {t("rideEditor.numericGrid.outOfSection")}
                        </Typography>
                      </TableCell>
                    );
                  }
                  const editable = deriveSheetState(s) === "OPEN";
                  const value = findMovementScore(s, m.movementNumber);
                  const gavelAriaLabel = t("rideEditor.numericGrid.gavelAriaLabel", {
                    movement: m.movementNumber,
                    position: judgePositionFor(klass, s.judgeId) || s.judgeId,
                    key: GAVEL_KEY.toUpperCase(),
                  });
                  return (
                    <TableCell key={s.id} data-testid={`numeric-only-grid-score-cell-${s.id}-${m.movementNumber}`}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <ScoreEntry
                          value={value}
                          disabled={!editable}
                          coefficient={m.coefficient ?? 1}
                          step={scoreStepFor(test, m.section)}
                          strict
                          invalidLabel={t("rideEditor.scoreInvalid")}
                          onChange={(score) => setMovementScoreFor(s.id, m.movementNumber, score === undefined ? "" : score)}
                          gridCell
                          inputRef={setCellRef(s.id, m.movementNumber)}
                          onKeyDown={(e) => {
                            // Card #2683 (AC12/AC13) — "g" always opens this
                            // cell's gavel modal and is always preventDefault'd,
                            // so it never types into the score field. Every
                            // other key falls through to focusNextGridCell,
                            // which acts on "Enter" AND, per #2714's
                            // `getOrderedCells`, "Tab" too — both walking the
                            // vertical (down-a-sheet) order; digits, ".", "-"
                            // and arrow keys are all still untouched.
                            if (e.key.toLowerCase() === GAVEL_KEY && !e.ctrlKey && !e.altKey && !e.metaKey) {
                              e.preventDefault();
                              openGavel(s.id, m.movementNumber);
                              return;
                            }
                            focusNextGridCell(e, { getOrderedCells: orderedGridCells });
                          }}
                        />
                        {editable && (
                          <Tooltip title={gavelAriaLabel}>
                            <IconButton
                              size="small"
                              tabIndex={-1}
                              onClick={() => openGavel(s.id, m.movementNumber)}
                              aria-label={gavelAriaLabel}
                              data-testid={`numeric-only-grid-gavel-${s.id}-${m.movementNumber}`}
                            >
                              <GavelIcon
                                fontSize="small"
                                color={hasEntryOnMovement(s, m.movementNumber) ? "error" : "disabled"}
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                      {isScoredZero(value) && (
                        <Chip
                          size="small"
                          label={t("rideEditor.scoredZero")}
                          color="error"
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}

            {/* #2676 — same subtotal treatment #2015 gave RideEditor.jsx's
                tabbed editor, brought to this grid's paper-scoring branch:
                one weightedMovementTotal value PER booth/judge column,
                reading straight off each sheet in `sheets` (this component's
                live entry state, not a frozen server total) so a
                partially-scored sheet's subtotal is simply the sum of
                what's entered so far. */}
            <TableRow data-testid="numeric-only-grid-movements-subtotal-row">
              <TableCell>
                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 700 }}>
                  {t("rideEditor.numericGrid.movementsSubtotal")}
                </Typography>
              </TableCell>
              {sheets.map((s) => (
                <TableCell key={s.id}>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ fontWeight: 700 }}
                    data-testid={`numeric-only-grid-movements-subtotal-value-${s.id}`}
                  >
                    {formatPoints(weightedMovementTotal(s, test), test)}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>

            {collectives.length > 0 && (
              <TableRow>
                <TableCell colSpan={sheets.length + 1} sx={{ fontWeight: 700, bgcolor: "action.hover" }}>
                  {test.collectivesLabel || t("rideEditor.numericGrid.collectivesHeader")}
                </TableCell>
              </TableRow>
            )}
            {collectives.map((c) => {
              const key = String(c.movementNumber);
              return (
                <TableRow key={`collective-${key}`} data-testid={`numeric-only-grid-collective-row-${c.movementNumber}`}>
                  <TableCell>
                    <Typography variant="body2" data-testid={`numeric-only-grid-collective-desc-${c.movementNumber}`}>
                      {c.name}
                    </Typography>
                  </TableCell>
                  {sheets.map((s) => {
                    const editable = deriveSheetState(s) === "OPEN";
                    const value = s.collectives?.[key]?.score;
                    return (
                      <TableCell key={s.id} data-testid={`numeric-only-grid-collective-score-cell-${s.id}-${c.movementNumber}`}>
                        <ScoreEntry
                          value={value}
                          disabled={!editable}
                          coefficient={c.coefficient ?? 1}
                          step={scoreStepFor(test)}
                          strict
                          invalidLabel={t("rideEditor.scoreInvalid")}
                          onChange={(score) => setCollectiveScoreFor(s.id, key, score === undefined ? "" : score)}
                          gridCell
                          inputRef={setCollectiveCellRef(s.id, c.movementNumber)}
                          onKeyDown={(e) => focusNextGridCell(e, { getOrderedCells: orderedGridCells })}
                        />
                        {isScoredZero(value) && (
                          <Chip
                            size="small"
                            label={t("rideEditor.scoredZero")}
                            color="error"
                            variant="outlined"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}

            {collectives.length > 0 && (
              // #2676 — same treatment as the movements subtotal row above,
              // reading weightedCollectiveTotal per booth/judge sheet; only
              // rendered when this test actually has a collectives block
              // (matching the collectivesHeader row's own conditional).
              <TableRow data-testid="numeric-only-grid-collectives-subtotal-row">
                <TableCell>
                  <Typography variant="body2" color="text.primary" sx={{ fontWeight: 700 }}>
                    {t("rideEditor.numericGrid.collectivesSubtotal")}
                  </Typography>
                </TableCell>
                {sheets.map((s) => (
                  <TableCell key={s.id}>
                    <Typography
                      variant="body2"
                      color="text.primary"
                      sx={{ fontWeight: 700 }}
                      data-testid={`numeric-only-grid-collectives-subtotal-value-${s.id}`}
                    >
                      {formatPoints(weightedCollectiveTotal(s, test), test)}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            )}

            {/* #2675 — eliminations/errors are deductions applied to the
                whole sheet, not another collective mark a judge awards.
                This row (and errors-row right below it) always renders,
                same as before this card — only the sx here changes, a
                border-top + extra top padding that reads as a group
                boundary against the collectives block above. Applied to
                this row's cells only (unconditional, not data-dependent)
                so a clean sheet with neither an elimination nor an error
                gets the exact same border/spacing as one that has both —
                no extra row, no conditional gap, nothing new to be
                "empty" (AC2). */}
            <TableRow data-testid="numeric-only-grid-eliminations-row">
              <TableCell sx={{ borderTop: "2px solid", borderColor: "divider", pt: 3 }}>
                {t("rideEditor.numericGrid.eliminationsRowLabel")}
              </TableCell>
              {sheets.map((s) => {
                const editable = deriveSheetState(s) === "OPEN";
                return (
                  <TableCell
                    key={s.id}
                    data-testid={`numeric-only-grid-eliminations-${s.id}`}
                    sx={{ borderTop: "2px solid", borderColor: "divider", pt: 3 }}
                  >
                    <Stack spacing={0.5} alignItems="flex-start">
                      {(s.eliminations ?? []).map((e) => (
                        <Chip
                          key={e.id}
                          size="small"
                          label={e.reason}
                          color="error"
                          variant="outlined"
                          onDelete={editable ? () => removeEliminationFor(s.id, e.id) : undefined}
                          data-testid={`numeric-only-grid-elimination-chip-${s.id}-${e.id}`}
                        />
                      ))}
                      {editable && (
                        <Button
                          size="small"
                          data-testid={`numeric-only-grid-add-elimination-${s.id}`}
                          onClick={(e) => openElimMenu(e, s.id)}
                        >
                          {t("rideEditor.numericGrid.addElimination")}
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                );
              })}
            </TableRow>

            <TableRow data-testid="numeric-only-grid-errors-row">
              <TableCell>{t("rideEditor.numericGrid.errorsRowLabel")}</TableCell>
              {sheets.map((s) => {
                const editable = deriveSheetState(s) === "OPEN";
                return (
                  <TableCell key={s.id} data-testid={`numeric-only-grid-errors-${s.id}`}>
                    <Stack spacing={0.5} alignItems="flex-start">
                      {(s.errors ?? []).map((e) => (
                        <Chip
                          key={e.id}
                          size="small"
                          label={errorLabel(e)}
                          variant="outlined"
                          onDelete={editable ? () => removeErrorFor(s.id, e.id) : undefined}
                          data-testid={`numeric-only-grid-error-chip-${s.id}-${e.id}`}
                        />
                      ))}
                      {editable && (
                        <Button
                          size="small"
                          data-testid={`numeric-only-grid-add-error-${s.id}`}
                          onClick={(e) => openErrorPopover(e, s.id)}
                        >
                          {t("rideEditor.numericGrid.addError")}
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                );
              })}
            </TableRow>

            {/* #2677 — the bottom line: final raw score + percentage, one
                value per booth, below the collective marks (above) and the
                eliminations/errors rows (immediately above). Reuses the
                exact rideEditor.rawScore string shape the non-paper branch
                uses (AC3) so the wording matches across both renderers.
                calculateScoreSheet's own `eliminated` (driven by this
                sheet's `eliminations`) gates which text renders — an
                eliminated booth prints "ELIMINATED" (the same
                rideEditor.eliminatedLabel string RideEditor.jsx's
                deductions list uses), never a percentage as if the ride
                scored (AC5). A model with no computable total (e.g.
                judge-percentage, no movements at all) renders nothing for
                that cell rather than a broken "undefined / undefined". */}
            <TableRow data-testid="numeric-only-grid-final-score-row">
              <TableCell>
                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 700 }}>
                  {t("rideEditor.numericGrid.finalScoreRowLabel")}
                </Typography>
              </TableCell>
              {sheets.map((s) => {
                const result = calculateScoreSheet(s, test);
                return (
                  <TableCell key={s.id}>
                    {result.eliminated ? (
                      <Typography
                        variant="body2"
                        color="error.main"
                        sx={{ fontWeight: 700 }}
                        data-testid={`numeric-only-grid-final-score-eliminated-${s.id}`}
                      >
                        {t("rideEditor.eliminatedLabel")}
                      </Typography>
                    ) : (
                      typeof result.totalPoints === "number" && (
                        <Typography
                          variant="body2"
                          color="text.primary"
                          sx={{ fontWeight: 700 }}
                          data-testid={`numeric-only-grid-final-score-value-${s.id}`}
                        >
                          {t("rideEditor.rawScore", {
                            points: formatPoints(result.totalPoints, test),
                            max: formatPoints(result.maxPoints, test),
                            percentage: formatPercentage(result.percentage),
                          })}
                        </Typography>
                      )
                    )}
                  </TableCell>
                );
              })}
            </TableRow>

            <TableRow data-testid="numeric-only-grid-save-row">
              <TableCell />
              {sheets.map((s) => {
                const editable = deriveSheetState(s) === "OPEN";
                return (
                  <TableCell key={s.id}>
                    {editable && (
                      <>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            loading={savingSheetId === s.id}
                            data-testid={`numeric-only-grid-save-${s.id}`}
                            onClick={() => onSaveSheet(s.id)}
                          >
                            {t("rideEditor.save")}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            loading={confirmingSheetId === s.id}
                            data-testid={`numeric-only-grid-confirm-${s.id}`}
                            onClick={() => onConfirmSheet(s.id)}
                          >
                            {t("rideEditor.numericGrid.confirm")}
                          </Button>
                        </Stack>
                        {saveErrors?.[s.id] && (
                          <Alert severity="error" sx={{ mt: 0.5 }} data-testid={`numeric-only-grid-save-error-${s.id}`}>
                            {t("rideEditor.numericGrid.sheetSaveError", {
                              position: judgePositionFor(klass, s.judgeId) || s.judgeId,
                            })}
                          </Alert>
                        )}
                        {/* #2467 (D1) — a save_failed confirm error is deliberately NOT
                            rendered here: confirmGridSheet always saves before it gates
                            or confirms, so that failure already set saveErrors?.[s.id]
                            above, which renders its own "Failed to save" alert — this
                            would only duplicate it. save_failed still counts toward the
                            page-level confirmFailedPositions banner below, since confirm
                            genuinely did not happen. */}
                        {confirmErrors?.[s.id] && confirmErrors[s.id].reason !== "save_failed" && (
                          <Alert severity="error" sx={{ mt: 0.5 }} data-testid={`numeric-only-grid-confirm-error-${s.id}`}>
                            {t(
                              confirmErrors[s.id].reason === "missing_score"
                                ? "rideEditor.numericGrid.sheetConfirmMissingScoreError"
                                : "rideEditor.numericGrid.sheetConfirmError",
                              { position: judgePositionFor(klass, s.judgeId) || s.judgeId },
                            )}
                          </Alert>
                        )}
                      </>
                    )}
                    {/* #2469 — the confirm freeze is real (deriveSheetState
                        !== OPEN disables every cell above), so this is the
                        grid's ONLY affordance for a mis-keyed CONFIRMED/
                        SIGNED booth: it hands the operator to the tabbed
                        editor's existing #1067 unlock/draft/commit lifecycle
                        for just this one sheet, rather than reimplementing
                        any part of that lifecycle here. */}
                    {!editable && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        data-testid={`numeric-only-grid-correct-${s.id}`}
                        onClick={() => onCorrectSheet(s.id)}
                      >
                        {t("rideEditor.numericGrid.correctSheet")}
                      </Button>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={elimMenu?.anchorEl} open={Boolean(elimMenu)} onClose={closeElimMenu}>
        {ELIMINATION_REASONS.map((reason) => (
          <MenuItem key={reason} onClick={() => addEliminationFor(elimMenu.sheetId, reason)}>
            {reason}
          </MenuItem>
        ))}
      </Menu>

      <Popover
        anchorEl={errorPopover?.anchorEl}
        open={Boolean(errorPopover)}
        onClose={closeErrorPopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 2, width: 320 }} data-testid="numeric-only-grid-error-popover">
          <RadioGroup value={errorMode} onChange={(e) => setErrorMode(e.target.value)}>
            <FormControlLabel
              value={ERROR_AMOUNT_SOURCES.RULE}
              control={<Radio size="small" disabled={!hasErrorConcept} />}
              label={t("rideEditor.numericGrid.errorEntryModeRule")}
            />
            <FormControlLabel
              value={ERROR_AMOUNT_SOURCES.MANUAL}
              control={<Radio size="small" />}
              label={t("rideEditor.numericGrid.errorEntryModeManual")}
            />
          </RadioGroup>
          {!hasErrorConcept && errorMode === ERROR_AMOUNT_SOURCES.RULE && (
            <Typography variant="caption" color="text.secondary">
              {t("rideEditor.numericGrid.noErrorConcept")}
            </Typography>
          )}
          {errorMode === ERROR_AMOUNT_SOURCES.MANUAL && (
            <Stack spacing={1} sx={{ mt: 1 }}>
              <TextField
                size="small"
                label={t("rideEditor.errorLabelPrompt")}
                placeholder={t("rideEditor.numericGrid.customErrorLabelPlaceholder")}
                value={errorLabelDraft}
                onChange={(e) => setErrorLabelDraft(e.target.value)}
                data-testid="numeric-only-grid-error-label-input"
              />
              <Stack direction="row" spacing={1}>
                <Select
                  size="small"
                  value={errorAmountType}
                  onChange={(e) => setErrorAmountType(e.target.value)}
                  data-testid="numeric-only-grid-error-amount-type"
                >
                  <MenuItem value="points">{t("rideEditor.numericGrid.customErrorAmountTypePoints")}</MenuItem>
                  <MenuItem value="percent">{t("rideEditor.numericGrid.customErrorAmountTypePercent")}</MenuItem>
                </Select>
                <TextField
                  size="small"
                  type="number"
                  label={t("rideEditor.errorAmountLabel")}
                  value={errorAmountValue}
                  onChange={(e) => setErrorAmountValue(e.target.value)}
                  data-testid="numeric-only-grid-error-amount-input"
                />
              </Stack>
            </Stack>
          )}
          <Button
            sx={{ mt: 2 }}
            variant="contained"
            size="small"
            onClick={confirmAddError}
            disabled={
              // #1068 fix (mirrored from RideEditor.jsx's error-dialog confirm
              // button) - rule mode has nothing to type, so the only way it
              // can't proceed is a sheet with no error-of-course schedule at
              // all, and the radio is already disabled for that. Manual mode
              // needs a label and an amount that score-common's errors[]
              // (z.number().nonnegative(), percent capped at 100) will
              // actually accept, or the PUT 400s and the whole booth's sheet
              // fails to save behind a generic error.
              errorMode === ERROR_AMOUNT_SOURCES.RULE
                ? !hasErrorConcept
                : !errorLabelDraft.trim() ||
                  errorAmountValue === "" ||
                  Number.isNaN(Number(errorAmountValue)) ||
                  Number(errorAmountValue) < 0 ||
                  (errorAmountType === "percent" && Number(errorAmountValue) > 100)
            }
            data-testid="numeric-only-grid-error-confirm"
          >
            {t("rideEditor.numericGrid.addErrorConfirm")}
          </Button>
        </Box>
      </Popover>

      {/* Card #2683 — the per-movement gavel modal. A Dialog, not a Menu/
          Popover (the card's own wording), since it holds a multi-step
          sub-form (menu -> deduction/custom). disableRestoreFocus is
          REQUIRED: MUI's default restore-focus targets whatever element was
          document.activeElement when the Dialog opened, which for a mouse
          click is the gavel IconButton itself, not the score box — wrong
          per AC14. closeGavel above does the real, correct focus return
          instead, off the same cellRefs map the "g" key path also uses.
          #2684 (https://board.foxvillage.com/tasks/2925) adds the "Recorded
          on this movement" block INSIDE the step==="menu" render below, per
          the card's own description ("Below the options, the modal shows a
          'Recorded on this movement' block") and its linked design artifact,
          both of which show the recorded list under the add-entry options
          in the SAME view, not a separate step — reading
          sheet.errors/eliminations filtered by movementNumber off the
          gavelSheet/gavelModal closure variables above and wiring the
          already-defined removeErrorFor/removeEliminationFor. */}
      <Dialog
        open={Boolean(gavelModal)}
        onClose={closeGavel}
        disableRestoreFocus
        maxWidth="xs"
        fullWidth
        data-testid="numeric-only-grid-gavel-modal"
      >
        <DialogTitle data-testid="numeric-only-grid-gavel-title">
          {t("rideEditor.numericGrid.gavelModalTitle", {
            movement: gavelModal?.movementNumber,
            position: gavelPosition,
          })}
          <Typography component="span" variant="body2" color="text.secondary" display="block" data-testid="numeric-only-grid-gavel-subtitle">
            {gavelMovement?.description}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {gavelModal?.step === "menu" && (
            <Box data-testid="numeric-only-grid-gavel-menu">
              <Typography variant="overline" color="text.secondary" display="block">
                {t("rideEditor.numericGrid.gavelRecordPenaltyOverline")}
              </Typography>
              {hasErrorConcept && (
                <MenuItem
                  onClick={() => addRuleErrorFor(gavelModal.sheetId, gavelModal.movementNumber)}
                  data-testid="numeric-only-grid-gavel-rule-error"
                >
                  {t("rideEditor.numericGrid.gavelErrorOfCourse", { tier: tierPreviewLabel(gavelTier, t) })}
                </MenuItem>
              )}
              <MenuItem
                onClick={() => setGavelModal((prev) => (prev ? { ...prev, step: "deduction" } : prev))}
                data-testid="numeric-only-grid-gavel-manual-deduction"
              >
                {isPercentErrorScheme
                  ? t("rideEditor.numericGrid.gavelPercentDeduction")
                  : t("rideEditor.numericGrid.gavelPointDeduction")}
              </MenuItem>
              <Divider sx={{ my: 1 }} />
              <Typography variant="overline" color="text.secondary" display="block">
                {t("rideEditor.numericGrid.gavelEliminateRideOverline")}
              </Typography>
              <Typography
                variant="caption"
                color="error.main"
                display="block"
                sx={{ mb: 1 }}
                data-testid="numeric-only-grid-gavel-eliminate-warning"
              >
                {t("rideEditor.numericGrid.gavelEliminateWarning")}
              </Typography>
              {ELIMINATION_REASONS.map((reason, idx) => (
                <MenuItem
                  key={reason}
                  onClick={() => addEliminationFor(gavelModal.sheetId, reason, gavelModal.movementNumber)}
                  data-testid={`numeric-only-grid-gavel-elim-${idx}`}
                >
                  {reason}
                </MenuItem>
              ))}
              <MenuItem
                onClick={() => setGavelModal((prev) => (prev ? { ...prev, step: "custom" } : prev))}
                data-testid="numeric-only-grid-gavel-custom"
              >
                {t("rideEditor.numericGrid.gavelCustomReason")}
              </MenuItem>

              {/* Card #2684 — the other half of this modal: what is already
                  recorded on THIS judge's THIS movement, below the
                  add-entry options above, each entry individually
                  removable. Reads gavelRecordedErrors/gavelRecordedEliminations
                  (derived above, same sheet.errors/eliminations arrays the
                  sheet-level rows already render, filtered by
                  gavelModal.movementNumber) and wires the already-defined
                  removeErrorFor/removeEliminationFor — no new handler, no
                  new prop, same Dialog/gavelModal state #2683 left. */}
              <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }} data-testid="numeric-only-grid-gavel-recorded">
                <Typography variant="overline" color="text.secondary" display="block">
                  {t("rideEditor.numericGrid.gavelRecordedOverline")}
                </Typography>
                {gavelRecordedErrors.length === 0 && gavelRecordedEliminations.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                    data-testid="numeric-only-grid-gavel-recorded-empty"
                  >
                    {t("rideEditor.numericGrid.gavelRecordedEmpty")}
                  </Typography>
                ) : (
                  <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" data-testid="numeric-only-grid-gavel-recorded-chips">
                    {gavelRecordedErrors.map((e) => (
                      <Chip
                        key={e.id}
                        size="small"
                        label={errorLabel(e)}
                        color="warning"
                        variant="outlined"
                        onDelete={() => removeErrorFor(gavelModal.sheetId, e.id)}
                        data-testid={`numeric-only-grid-gavel-recorded-error-${e.id}`}
                      />
                    ))}
                    {gavelRecordedEliminations.map((e) => (
                      <Chip
                        key={e.id}
                        size="small"
                        label={e.reason}
                        color="error"
                        variant="outlined"
                        onDelete={() => removeEliminationFor(gavelModal.sheetId, e.id)}
                        data-testid={`numeric-only-grid-gavel-recorded-elimination-${e.id}`}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            </Box>
          )}
          {gavelModal?.step === "deduction" && (
            <Stack spacing={2} sx={{ mt: 1 }} data-testid="numeric-only-grid-gavel-deduction">
              <TextField
                size="small"
                autoFocus
                label={t("rideEditor.numericGrid.gavelDeductionReasonLabel")}
                value={gavelModal.deductionReason}
                onChange={(e) => setGavelModal((prev) => (prev ? { ...prev, deductionReason: e.target.value } : prev))}
                data-testid="numeric-only-grid-gavel-deduction-reason"
              />
              <TextField
                size="small"
                type="number"
                label={t("rideEditor.numericGrid.gavelDeductionAmountLabel")}
                value={gavelModal.deductionAmount}
                onChange={(e) => setGavelModal((prev) => (prev ? { ...prev, deductionAmount: e.target.value } : prev))}
                data-testid="numeric-only-grid-gavel-deduction-amount"
              />
            </Stack>
          )}
          {gavelModal?.step === "custom" && (
            <Stack spacing={2} sx={{ mt: 1 }} data-testid="numeric-only-grid-gavel-custom-step">
              <TextField
                size="small"
                autoFocus
                label={t("rideEditor.numericGrid.gavelCustomReasonLabel")}
                value={gavelModal.customReason}
                onChange={(e) => setGavelModal((prev) => (prev ? { ...prev, customReason: e.target.value } : prev))}
                data-testid="numeric-only-grid-gavel-custom-reason"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {gavelModal?.step !== "menu" && (
            <Button onClick={backToGavelMenu} data-testid="numeric-only-grid-gavel-back">
              {t("rideEditor.numericGrid.gavelBack")}
            </Button>
          )}
          {gavelModal?.step === "deduction" && (
            <Button
              variant="contained"
              onClick={() =>
                addManualDeductionFor(
                  gavelModal.sheetId,
                  gavelModal.movementNumber,
                  gavelModal.deductionReason.trim(),
                  isPercentErrorScheme ? "percent" : "points",
                  Number(gavelModal.deductionAmount),
                )
              }
              disabled={
                !gavelModal?.deductionReason.trim() ||
                gavelModal?.deductionAmount === "" ||
                Number.isNaN(Number(gavelModal?.deductionAmount)) ||
                Number(gavelModal?.deductionAmount) < 0 ||
                (isPercentErrorScheme && Number(gavelModal?.deductionAmount) > 100)
              }
              data-testid="numeric-only-grid-gavel-deduction-confirm"
            >
              {t("rideEditor.numericGrid.gavelDeductionConfirm")}
            </Button>
          )}
          {gavelModal?.step === "custom" && (
            <Button
              variant="contained"
              color="error"
              onClick={() => addEliminationFor(gavelModal.sheetId, gavelModal.customReason.trim(), gavelModal.movementNumber)}
              disabled={!gavelModal?.customReason.trim()}
              data-testid="numeric-only-grid-gavel-custom-confirm"
            >
              {t("rideEditor.numericGrid.gavelCustomReasonConfirm")}
            </Button>
          )}
          <Button onClick={closeGavel} data-testid="numeric-only-grid-gavel-done">
            {t("rideEditor.numericGrid.gavelDone")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
